import {
  BaseLayoutManager,
  detectMode,
  detectOrientation,
} from '@bn-games/layout-core';
import type { DominoPlayer } from '../game/types.js';
import { seatsFor } from '../game/types.js';
import type { PlayerCount } from '../game/variants/types.js';
import type { ChainRegion } from '../render/BoardRenderer.js';
import type { HandZone } from '../render/HandRenderer.js';
import {
  BOARD_BORDER_THICKNESS,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  HAND_ZONE_INNER_GAP,
  HAND_ZONE_THICKNESS,
} from '../utils/constants.js';
import type { LayoutConfig } from './types.js';

/**
 * Inputs the LayoutManager needs from the rest of the app. Player count
 * and local seat change when the user starts a new game / picks a seat;
 * the manager re-computes the layout in response and notifies subscribers.
 */
export interface LayoutInputs {
  playerCount: PlayerCount;
  localSeat: number;
}

const PAGE_MARGIN_DESKTOP = 32;
// Edge-to-edge on phones/tablets — backgammon uses 0 here too, and the
// non-zero margin made domino look "not fullscreen" relative to it.
const PAGE_MARGIN_MOBILE = 0;

/**
 * Maps a logical seat index (0..playerCount-1, in clockwise seating order
 * starting from `seatsFor(playerCount)[0]`) to a position around the
 * table from the LOCAL player's perspective. The local player always
 * occupies south; the other seats fill west/north/east in clockwise order
 * starting from the seat immediately to the local player's left.
 */
function tablePositions(
  playerCount: PlayerCount,
  localSeat: number,
): Readonly<Record<DominoPlayer, number | null>> {
  const seatOrder = seatsFor(playerCount);
  // Clockwise order of physical positions around the table.
  const positions: readonly DominoPlayer[] =
    playerCount === 2
      ? ['south', 'north']
      : playerCount === 3
        ? ['south', 'west', 'north']
        : ['south', 'west', 'north', 'east'];
  const result: Record<DominoPlayer, number | null> = {
    south: null,
    west: null,
    north: null,
    east: null,
  };
  for (let i = 0; i < playerCount; i += 1) {
    const physical = positions[i]!;
    const logicalSeat = (localSeat + i) % playerCount;
    // logicalSeat is the engine seat index for `physical`. We map physical
    // position → seatOrder[logicalSeat] so the renderer can ask "which
    // engine seat sits in the west zone?" via this table.
    result[physical] = logicalSeat;
    // Avoid unused-var by referencing seatOrder.
    void seatOrder[logicalSeat];
  }
  return result;
}

const DEFAULT_INPUTS: LayoutInputs = { playerCount: 2, localSeat: 0 };

export class LayoutManager extends BaseLayoutManager<LayoutConfig> {
  /**
   * Initialized via a field initializer, but `BaseLayoutManager.constructor`
   * calls `this.compute()` *before* subclass field initializers run (the
   * field is defined after `super()` under `useDefineForClassFields: true`).
   * `compute()` reads via `inputsOrDefault()` so the first call works without
   * NPE.
   */
  private inputs: LayoutInputs = DEFAULT_INPUTS;

  setInputs(inputs: Partial<LayoutInputs>): void {
    const current = this.inputsOrDefault();
    let changed = false;
    let next = current;
    if (inputs.playerCount !== undefined && inputs.playerCount !== current.playerCount) {
      next = { ...next, playerCount: inputs.playerCount };
      changed = true;
    }
    if (inputs.localSeat !== undefined && inputs.localSeat !== current.localSeat) {
      next = { ...next, localSeat: inputs.localSeat };
      changed = true;
    }
    this.inputs = next;
    if (changed) this.refresh();
  }

  private inputsOrDefault(): LayoutInputs {
    return this.inputs ?? DEFAULT_INPUTS;
  }

  protected compute(): LayoutConfig {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const mode = detectMode(viewportWidth);
    const orientation = detectOrientation(viewportWidth, viewportHeight);
    // Rotate the 16:9 table 90° on portrait phones / tablets so the canvas
    // fills the screen instead of floating tiny at the top. Mirrors what
    // backgammon does for its 3:2 board.
    const shouldRotateBoard =
      mode !== 'desktop' && orientation === 'portrait';
    // Post-swap logical dimensions: in rotated mode the canvas is "tall",
    // so width = CANVAS_HEIGHT, height = CANVAS_WIDTH.
    const canvasWidth = shouldRotateBoard ? CANVAS_HEIGHT : CANVAS_WIDTH;
    const canvasHeight = shouldRotateBoard ? CANVAS_WIDTH : CANVAS_HEIGHT;
    const margin = mode === 'desktop' ? PAGE_MARGIN_DESKTOP : PAGE_MARGIN_MOBILE;
    const usableW = Math.max(0, viewportWidth - margin * 2);
    const usableH = Math.max(0, viewportHeight - margin * 2);
    // Fit-to-smaller-axis with no upper cap so the canvas grows on big
    // monitors (a 32" display would otherwise leave the 1280×720 table
    // floating in the middle with lots of whitespace).
    const scale = Math.min(usableW / canvasWidth, usableH / canvasHeight);
    const displayWidth = Math.floor(canvasWidth * scale);
    const displayHeight = Math.floor(canvasHeight * scale);
    const dpr = window.devicePixelRatio || 1;

    const { playerCount, localSeat } = this.inputsOrDefault();
    const seats = seatsFor(playerCount);
    const { chainRegion, seatZones } = computeZones(playerCount);

    return {
      mode,
      orientation,
      viewportWidth,
      viewportHeight,
      shouldRotateBoard,
      canvasWidth,
      canvasHeight,
      displayWidth,
      displayHeight,
      devicePixelRatio: dpr,
      playerCount,
      localSeat,
      seats,
      chainRegion,
      seatZones,
    };
  }

  protected equals(a: LayoutConfig, b: LayoutConfig): boolean {
    return (
      a.mode === b.mode &&
      a.orientation === b.orientation &&
      a.viewportWidth === b.viewportWidth &&
      a.viewportHeight === b.viewportHeight &&
      a.shouldRotateBoard === b.shouldRotateBoard &&
      a.canvasWidth === b.canvasWidth &&
      a.canvasHeight === b.canvasHeight &&
      a.displayWidth === b.displayWidth &&
      a.displayHeight === b.displayHeight &&
      a.devicePixelRatio === b.devicePixelRatio &&
      a.playerCount === b.playerCount &&
      a.localSeat === b.localSeat
    );
  }
}

/**
 * Compute the chain region + per-seat hand zones in canvas-internal coords.
 * The hand-zone thickness is fixed; the chain region is whatever's left
 * after carving out hand zones around the edges.
 *
 * - 2 players: south + north (no side zones; chain spreads wider).
 * - 3 players: south + west + north (no east).
 * - 4 players: all four sides.
 */
export function computeZones(playerCount: PlayerCount): {
  chainRegion: import('../render/BoardRenderer.js').ChainRegion;
  seatZones: Readonly<Record<DominoPlayer, HandZone | null>>;
} {
  const thick = HAND_ZONE_THICKNESS;
  const border = BOARD_BORDER_THICKNESS;
  const gap = HAND_ZONE_INNER_GAP;

  // Everything sits inside the wooden border frame. South + north are always
  // present; west/east appear for 3p / 4p. Between each occupied tray and
  // the chain region we leave `gap` of bare wood — combined with the inset
  // bevel painted around the chain region, this reads as "table sunk below
  // the surrounding shelf".
  let chainX = border;
  let chainY = border;
  let chainW = CANVAS_WIDTH - border * 2;
  let chainH = CANVAS_HEIGHT - border * 2;

  chainY += thick + gap;
  chainH -= (thick + gap) * 2;

  if (playerCount >= 3) {
    chainX += thick + gap;
    chainW -= thick + gap;
  }
  if (playerCount >= 4) {
    chainW -= thick + gap;
  }

  const chainRegion: ChainRegion = { x: chainX, y: chainY, width: chainW, height: chainH };

  const innerLeft = border;
  const innerRight = CANVAS_WIDTH - border;
  const innerTop = border;
  const innerBottom = CANVAS_HEIGHT - border;
  // Hand-zone rows hug the inner edge of the frame; side zones sit between
  // the row trays without spilling under them.
  const sideRowX = innerLeft;
  const sideRowWidth = innerRight - innerLeft;

  const zones: Record<DominoPlayer, HandZone | null> = {
    south: {
      seat: 'south',
      x: sideRowX,
      y: innerBottom - thick,
      width: sideRowWidth,
      height: thick,
    },
    north: {
      seat: 'north',
      x: sideRowX,
      y: innerTop,
      width: sideRowWidth,
      height: thick,
    },
    west:
      playerCount >= 3
        ? {
            seat: 'west',
            x: innerLeft,
            y: innerTop + thick,
            width: thick,
            height: innerBottom - innerTop - thick * 2,
          }
        : null,
    east:
      playerCount >= 4
        ? {
            seat: 'east',
            x: innerRight - thick,
            y: innerTop + thick,
            width: thick,
            height: innerBottom - innerTop - thick * 2,
          }
        : null,
  };
  return { chainRegion, seatZones: zones };
}

export { tablePositions };
